import { FC, useEffect, useState } from "react";
import "./ModalPricePlanInsert.scss";
import ModalDefault from "../../../ModalDefault/ModalDefault";
import { yupResolver } from "@hookform/resolvers/yup";
import { useAuth } from "../../../../providers/AuthProvider";
import { set, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { toast } from "react-toastify";
import { Card, Col, Dropdown, FormGroup, Row } from "react-bootstrap";
import ErrorMessage from "../../../ErrorMessage/ErrorMessage";
import { schema } from "./Validate";
import { useExample } from "../../../../providers/ExampleProvider";
import { pricePlans } from "../../../../models/Financial/OptionsValues";
import { customStyles } from "../../../../models/SelectCustomStyles";
import FormatMoney from "../../../../utils/FormatMoney";
import SkeletonTable from "../../../SkeletonTable/SkeletonTable";
import { usePrices } from "../../../../providers/Financial/Plans/Prices/PricesProvider";
import DropdownMenuTable from "../../../DropdownMenuTable/DropdownMenuTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import Swal from "sweetalert2";
import { PlansModel } from "../../../../models/Financial/Plans/PlansModel";

interface ModalPricePlanInsertProps {
  show: boolean;
  plan?: PlansModel;
  onClose: () => void;
}

const ModalPricePlanInsert: FC<ModalPricePlanInsertProps> = ({
  show,
  onClose,
  plan,
}) => {
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [selectedPricePlans, setSelectedPricePlans] = useState<any | null>(
    null,
  );
  const [amount, setAmount] = useState<any>("");

  const { onInvalid } = useAuth();
  const {
    setParams,
    params,
    isLoadingModal,
    prices,
    handleList,
    handleDelete,
    error,
    setError,
    handleFetch,
  } = usePrices();

  const {
    handleSubmit,
    register,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  const resetForm = () => {
    reset({
      id: null,
      cycle: "",
      price: 0,
      plan_id: plan?.id ?? 0,
    });

    setAmount(null);
    setSelectedPricePlans(null);
    setError("");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleEdit = (item: any) => {
    setValue("id", item?.id);
    setValue("cycle", item?.cycle);
    setValue("price", item?.price);
    setAmount(item?.price);
    setSelectedPricePlans(
      pricePlans.filter((x: any) => x.value === item?.cycle),
    );
  };

  const handleDeletePrice = (item: any) => {
    Swal.fire({
      title: "<strong>Atenção?</strong>",
      html: `<span>Deseja excluir?</span>`,
      icon: "question",
      confirmButtonText: "Sim, deletar!",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonColor: "var(--primary)",
    }).then(async (x: any) => {
      if (x.isConfirmed) {
        const ret = await handleDelete(item.id || 0).then();
        if (ret) {
          toast.success("Preço inativado com sucesso!");
          handleList();
        } else {
          await Swal.fire(
            "Ops!",
            `Não foi possível inativar o Preço.<br>${error}`,
            "error",
          );
        }
      }
    });
  };

  const onSubmit = async (data: any) => {
    setIsLoadingSubmit(true);
    setError("");

    const existingPrice = prices.some(
      (p) => p.cycle === selectedPricePlans?.value,
    );

    if (existingPrice) {
      toast.error("Já existe preço cadastrado para o tipo de cobrança!");
      setIsLoadingSubmit(false);
      return;
    }

    const ret = await handleFetch(data as any);

    if (ret) {
      toast.success("Preço salvo com sucesso!");
      handleList();
      resetForm();
    }

    setIsLoadingSubmit(false);
  };

  const handleAmount = (e: any) => {
    const inputValue: string = e.target.value.replace(/\D/g, "");
    const numericValue: number = parseFloat(inputValue) / 100;
    setAmount(numericValue);
    setValue("price", numericValue);
  };

  useEffect(() => {
    if (show) {
      if (plan?.id) {
        setValue("plan_id", plan?.id);
        setParams({
          ...params,
          page: 1,
          withPaginate: false,
          filter: { plan_id: plan?.id },
        });
      } else {
        resetForm();
      }
    } else {
      handleClose();
    }
  }, [show]);

  const renderList = () => {
    return (
      <>
        {prices?.length > 0 ? (
          <>
            {prices?.map((x: any, key: number) => (
              <tr key={x?.id} className="text-sm">
                <td className="text-center">{x?.id}</td>
                <td>{FormatMoney(x?.price)}</td>
                <td className="text-center">
                  {x?.cycle === "annual" ? "Anual" : "Mensal"}
                </td>
                <td className="text-center">
                  <span
                    className={
                      x?.deleted_at ? "badge bg-danger" : "badge bg-success"
                    }
                  >
                    {x?.deleted_at ? "Inativo" : "Ativo"}
                  </span>
                </td>
                <td>{x?.stripe_price_id}</td>
                <td className="text-center">
                  <DropdownMenuTable key={key}>
                    <Dropdown.Item
                      className="border-radius-md py-2"
                      onClick={() => handleEdit(x)}
                    >
                      <FontAwesomeIcon icon={faEdit} className="me-2" /> Editar
                    </Dropdown.Item>
                    {!x?.deleted_at && (
                      <Dropdown.Item
                        className="border-radius-md py-2"
                        onClick={() => handleDeletePrice(x)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="me-2" />{" "}
                        Remover
                      </Dropdown.Item>
                    )}
                  </DropdownMenuTable>
                </td>
              </tr>
            ))}
          </>
        ) : (
          <tr>
            <td colSpan={6} className="text-center text-sm">
              Não há registros
            </td>
          </tr>
        )}
      </>
    );
  };

  const renderContent = () => {
    return (
      <>
        {!isLoadingModal ? (
          <div>
            <div className="table-responsive">
              <table className="table table-flush m-0 w-100">
                <thead className="thead-light">
                  <tr>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      #
                    </th>
                    <th className="text-uppercase opacity-7" scope="col">
                      Preço
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Tipo de cobrança
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Status
                    </th>
                    <th
                      className="text-uppercase opacity-7"
                      scope="col"
                    >
                      Id Stripe
                    </th>
                    <th
                      className="text-uppercase opacity-7 text-center"
                      scope="col"
                    >
                      Ação
                    </th>
                  </tr>
                </thead>
                <tbody>{renderList()}</tbody>
              </table>
            </div>
          </div>
        ) : (
          <SkeletonTable />
        )}
      </>
    );
  };

  return (
    <ModalDefault
      title={"Adicionar Preço"}
      show={show}
      onClose={handleClose}
      sizeModal={"xl"}
      showFooter={true}
      buttonText={isLoadingSubmit ? "Salvando..." : "Salvar"}
      showButtonSpinner={isLoadingSubmit}
      disabledSubmit={isLoadingSubmit}
      handleSubmit={handleSubmit(onSubmit)}
      backdrop="static"
      backgroundColor="#f8f9fa"
    >
      <div className="ModalPricePlanInsert" data-testid="ModalPricePlanInsert">
        <Row>
          <Col md={4}>
            <FormGroup className="form-group">
              <label htmlFor="plan">Plano</label>
              <input
                className={`form-control`}
                value={plan?.name}
                type="text"
                disabled={true}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label htmlFor="cycle">Forma de cobrança</label>
              <ReactSelect
                isSearchable
                options={pricePlans}
                value={selectedPricePlans ?? ""}
                defaultValue={selectedPricePlans}
                placeholder="Selecione..."
                className={`form-control p-0`}
                noOptionsMessage={() => "Não há registros"}
                styles={customStyles}
                onChange={(val: any) => {
                  setValue("cycle", val.value);
                  setSelectedPricePlans(val);
                }}
              />

              <ErrorMessage
                name={"Forma de cobrança"}
                type={errors?.cycle?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={4}>
            <FormGroup className="form-group">
              <label htmlFor="price">Preços</label>
              <input
                className={`form-control ${onInvalid(errors?.price)}`}
                value={FormatMoney(amount || "")}
                type="text"
                onChange={(e: any) => handleAmount(e)}
              />

              <ErrorMessage
                name={"Preços"}
                type={errors?.price?.type?.toString()}
                min={3}
                max={100}
              />
            </FormGroup>
          </Col>
          <Col md={12}>
            <Card>
              <Card.Body>{renderContent()}</Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </ModalDefault>
  );
};

export default ModalPricePlanInsert;
